import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { transferService } from '@/features/booking/services/transferService';
import type { BookingTransfer } from '@/features/booking/types/transferTypes';
import type { BookingCategoryClass } from '@/features/booking/types/bookingTypes';
import { useAuth } from '@/hooks';

// --- Types ---
export type ViewMode = 'LOADING' | 'UNAUTH' | 'SENDER_MANAGE' | 'RECEIVER_ACCEPT' | 'COMPLETED_THIRD_PARTY' | 'ERROR';

export interface BookingDetails {
    id: string;
    timezone_id?: string;
    event_booking_category?: {
        id: string;
        price?: number;
        custom_slot_duration?: number | null;
        booking_category?: {
            name?: string;
            card_image_id?: string;
            is_table_booking?: boolean;
            class?: BookingCategoryClass;
            business?: {
                id: string;
                name?: string;
                handle?: string;
                description?: string;
                card_image_id?: string;
                image_id?: string;
                media?: { id: string; media_type: string }[] | null;
                location?: {
                    address_line1?: string;
                    city?: string;
                    state_province?: string;
                    country?: string;
                    time_zone?: string;
                };
            };
        };
        event_schedule?: {
            id: string;
            start_time?: string;
            duration?: number;
            event_id?: string;
            event?: {
                id: string;
                name?: string;
                description?: string;
                dress_code?: string;
                card_image_id?: string;
                image_id?: string;
            };
        };
    };
    add_ons?: Array<{
        quantity?: number;
        price?: number;
        add_on?: {
            id: string;
            name?: string;
            image_id?: string;
            price?: number;
        };
    }>;
    start_time?: string;
    booked_table_number?: string | null;
    booked_table_size?: number | null;
}

export interface UseTransferReturn {
    // Data
    transferData: BookingTransfer | null;
    bookingDetails: BookingDetails | null;
    role: 'SENDER' | 'RECEIVER' | null;
    viewMode: ViewMode;
    loading: boolean;
    error: string | null;

    // Countdown
    countdown: string;
    isExpiredByCountdown: boolean;

    // Actions
    handleAccept: () => Promise<void>;
    handleCancel: () => Promise<void>;
    handleCopyLink: () => void;

    // UI State
    copied: boolean;
    isAccepting: boolean;
    showCancelModal: boolean;
    setShowCancelModal: (show: boolean) => void;

    // Navigation
    handleNavigateToBooking: (bookingId?: string) => void;

    // Derived data for components
    displayStatus: string;
    isPending: boolean;
    isCompleted: boolean;
}

export function useTransfer(
    bookingId: string | undefined,
    transferId: string | undefined
): UseTransferReturn {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();

    // Core state
    const [viewMode, setViewMode] = useState<ViewMode>('LOADING');
    const [transferData, setTransferData] = useState<BookingTransfer | null>(null);
    const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
    const [role, setRole] = useState<'SENDER' | 'RECEIVER' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // UI state
    const [countdown, setCountdown] = useState("00:00:00");
    const [isExpiredByCountdown, setIsExpiredByCountdown] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [copied, setCopied] = useState(false);

    // Ref to prevent duplicate fetches
    const fetchingRef = useRef(false);
    const lastFetchParamsRef = useRef<string>('');

    // --- Data Fetching ---
    const fetchTransferData = useCallback(async () => {
        // Create a unique key for current params to avoid duplicate fetches
        const fetchKey = `${bookingId}-${transferId}-${isAuthenticated}`;

        // Skip if already fetching or same params
        if (fetchingRef.current || lastFetchParamsRef.current === fetchKey) {
            return;
        }

        if (!isAuthenticated) {
            // Redirect unauthenticated users to login with return URL
            const returnUrl = encodeURIComponent(window.location.pathname);
            navigate(`/login?returnUrl=${returnUrl}`);
            setLoading(false);
            return;
        }

        fetchingRef.current = true;
        lastFetchParamsRef.current = fetchKey;
        setLoading(true);
        setViewMode('LOADING');

        try {
            let transfer: BookingTransfer | null = null;
            let booking: BookingDetails | null = null;
            let detectedRole: 'SENDER' | 'RECEIVER' | null = null;

            // All transfers are now accessed via transferId
            // The transfer is created in TicketModal before navigation
            if (transferId) {
                transfer = await transferService.getTransferById(transferId);
                booking = transfer.booking as BookingDetails;

                if (transfer.sender?.id === user?.id) {
                    detectedRole = 'SENDER';
                } else {
                    detectedRole = 'RECEIVER';
                }
            } else if (bookingId) {
                // Legacy fallback: if someone accesses via bookingId, try to create/get transfer
                // This should rarely happen now since TicketModal creates the transfer first
                const createdTransfer = await transferService.createTransfer(bookingId);
                transfer = await transferService.getTransferById(createdTransfer.id);
                booking = transfer.booking as BookingDetails;
                detectedRole = 'SENDER';
            } else {
                throw new Error("Invalid URL parameters");
            }

            if (!transfer) throw new Error("Transfer not found");

            // Check if user is a 3rd party viewing a completed transfer
            const isSender = transfer.sender?.id === user?.id;
            const isReceiver = transfer.receiver?.id === user?.id;
            const isThirdParty = !isSender && !isReceiver;
            const isCompleted = transfer.status === 'COMPLETED';

            // If 3rd party views a completed transfer, show restricted view
            if (isThirdParty && isCompleted) {
                setTransferData(transfer);
                setBookingDetails(null); // Don't expose booking details
                setRole(null);
                setViewMode('COMPLETED_THIRD_PARTY');
                return;
            }

            setTransferData(transfer);
            setBookingDetails(booking);
            setRole(detectedRole);
            setViewMode(detectedRole === 'SENDER' ? 'SENDER_MANAGE' : 'RECEIVER_ACCEPT');

        } catch (err: any) {
            setError(err.message || "Failed to load transfer.");
            setViewMode('ERROR');
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [bookingId, transferId, isAuthenticated, user?.id]);

    // Fetch on mount and when key params change
    useEffect(() => {
        fetchTransferData();
    }, [fetchTransferData]);

    // --- Countdown Timer ---
    useEffect(() => {
        if (!transferData?.expires_at) return;

        const calculateCountdownValue = () => {
            const now = new Date().getTime();
            const expiryTime = new Date(transferData.expires_at).getTime();
            const diff = expiryTime - now;

            if (diff <= 0) {
                setCountdown("00:00:00");
                setIsExpiredByCountdown(true);
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            setCountdown(
                `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            );
        };

        calculateCountdownValue();
        const interval = setInterval(calculateCountdownValue, 1000);

        return () => clearInterval(interval);
    }, [transferData?.expires_at]);

    // --- Action Handlers ---
    const handleNavigateToBooking = useCallback((targetBookingId: string | undefined) => {
        if (targetBookingId) {
            const newParams = new URLSearchParams();
            newParams.set('view', 'tickets');
            newParams.set('bookingId', targetBookingId);
            navigate(`/profile?${newParams.toString()}`);
        } else {
            navigate('/profile?view=tickets');
        }
    }, [navigate]);

    const handleAccept = useCallback(async () => {
        if (!transferData?.id) return;
        try {
            setIsAccepting(true);
            await transferService.acceptTransfer(transferData.id);
            navigate('/profile?view=tickets');
        } catch (e: any) {
            toast.error(e.message || "Failed to accept transfer.");
        } finally {
            setIsAccepting(false);
        }
    }, [transferData?.id, navigate]);

    const handleCancel = useCallback(async () => {
        const id = bookingId || (transferData?.booking as any)?.id;
        if (!id) return;
        try {
            await transferService.cancelTransfer(id);
            setShowCancelModal(false);
            navigate(-1);
        } catch (e: any) {
            toast.error(e.message || "Failed to cancel transfer.");
        }
    }, [bookingId, transferData?.booking, navigate]);

    const handleCopyLink = useCallback(() => {
        const link = `${window.location.origin}/transfer-booking/t/${transferData?.id}`;
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [transferData?.id]);

    // --- Derived State ---
    const isTimeExpired = transferData?.expires_at
        ? new Date(transferData.expires_at) < new Date()
        : false;

    let displayStatus = transferData?.status || 'PENDING';
    if (displayStatus === 'PENDING' && (isTimeExpired || isExpiredByCountdown)) {
        displayStatus = 'EXPIRED';
    }

    const isPending = displayStatus === 'PENDING';
    const isCompleted = displayStatus === 'COMPLETED';

    return {
        // Data
        transferData,
        bookingDetails,
        role,
        viewMode,
        loading,
        error,

        // Countdown
        countdown,
        isExpiredByCountdown,

        // Actions
        handleAccept,
        handleCancel,
        handleCopyLink,

        // UI State
        copied,
        isAccepting,
        showCancelModal,
        setShowCancelModal,

        // Navigation
        handleNavigateToBooking,

        // Derived
        displayStatus,
        isPending,
        isCompleted,
    };
}
